import { useEffect, useMemo, useState } from "react";
import { get as lodashGet } from "lodash-es";
import { ComponentKey } from "@certa/studio-core/src/constants";
import type { HashMap } from "@certa/types";
import { create, useStore } from "zustand";
import {
  subscribeWithSelector,
  persist,
  createJSONStorage
} from "zustand/middleware";
import {
  stateManagerSlice,
  type StateManagerSlice
} from "../../dbOpsSlices/stateManager.slice";
import {
  emptyMetaJSONLookupData,
  lookupWorkerSlice,
  type LookupWorkerSlice
} from "../../dbOpsSlices/lookupWorker.slice";
import {
  emptyProcess,
  processSlice,
  type ProcessSlice
} from "../../dbOpsSlices/process.slice";
import {
  appsettingsSlice,
  type AppSettingsSlice,
  getAppSettingsFromMegaJSON
} from "../../dbOpsSlices/appsettings.slice";
import {
  getEnvironment,
  getAppSettings
} from "../../../utils/appSettings.providers";
import {
  type ProcessProps,
  type Rule,
  type ConditionData,
  type ConditionGroupData,
  type Field,
  type Step,
  type Swimlane
} from "@certa/studio-core";
import { persistentStorageForDBOpsStore } from "../../dbOps.persistentStorage.utils";
import { workerMessageQueue } from "../../processLookup/workerMessageQueue";
import {
  dataObjectsSlice,
  type DataObjectsSlice
} from "../../dbOpsSlices/dataObjects.slice";

function toArray<T>(value: HashMap<T>): T[] {
  return Object.values(value);
}

export type DBOpsState = ProcessSlice &
  StateManagerSlice &
  LookupWorkerSlice &
  AppSettingsSlice &
  DataObjectsSlice;

const dbOpsStore = create<DBOpsState>()(
  subscribeWithSelector(
    persist(
      (...args) => ({
        ...stateManagerSlice(...args),
        ...lookupWorkerSlice(...args),
        ...processSlice(...args),
        ...appsettingsSlice(...args),
        ...dataObjectsSlice(...args)
      }),
      {
        name: "dbOpsStore", // Doesn't matter, as we are not using it utilizing it inside hydration/setter logic
        storage: createJSONStorage(() => persistentStorageForDBOpsStore),
        partialize: state => ({
          megaJSON: state.megaJSON
        })
      }
    )
  )
);

export const dbOps = dbOpsStore.getState;

// Subscribe to megaJSON changes and update worker with debouncing
// This prevents memory issues from excessive structured cloning of large megaJSON objects
dbOpsStore.subscribe(
  state => state.megaJSON,
  async () => {
    const {
      megaJSONWorkerInstance,
      isECAEnabled,
      isFlowModeEnabled,
      megaJSON
    } = dbOps();

    if (!megaJSONWorkerInstance) return;

    // Use debounced queue to prevent memory pressure from rapid changes
    // This batches rapid edits and cancels intermediate updates
    workerMessageQueue.postDebounced(megaJSONWorkerInstance, {
      megaJSON,
      isECAEnabled,
      isFlowModeEnabled,
      appSettings: await getAppSettings({
        branchSavePointId: Number(megaJSON.id),
        skipMergingReferencedAppSettings: true
      }),
      environment: getEnvironment()
    });
  }
);

export const useMegaJSON = () => {
  return useStore(dbOpsStore, state => state.megaJSON);
};

export const useMetaJSONLookupSelector = (processId: string) => {
  const megaJSONLookupData = useStore(
    dbOpsStore,
    state => state.megaJSONLookUpData
  );
  return useMemo(() => {
    return megaJSONLookupData?.[processId] || emptyMetaJSONLookupData;
  }, [megaJSONLookupData, processId]);
};

/**
 * Returns live state from the in-memory process
 * If path is not provided, it returns the whole process object else the value at the path provided
 * @param path
 */
export function useInMemoryJSONSelector<T>({
  processId,
  path
}: {
  processId: string;
  path?: string;
}): T {
  // Use a more specific selector to ensure React detects changes
  const megaJSON = useStore(dbOpsStore, state => state.megaJSON);
  const [value, setValue] = useState<T>(() => {
    const process = megaJSON.processes[processId];
    if (!process) return (path ? null : emptyProcess) as T;

    if (path) {
      const val = lodashGet(process, path);
      return (typeof val === "object" ? { ...val } : val) as T;
    } else {
      return { ...process } as T;
    }
  });

  useEffect(() => {
    const process = megaJSON.processes[processId];
    if (!process) {
      setValue((path ? null : emptyProcess) as T);
      return;
    }

    if (path) {
      const val = lodashGet(process, path);
      setValue((typeof val === "object" ? { ...val } : val) as T);
    } else {
      setValue({ ...process } as T);
    }
  }, [megaJSON, processId, path]);

  return value;
}

export function useAppSettings() {
  const megaJSON = useStore(dbOpsStore, state => state.megaJSON);

  return useMemo(() => getAppSettingsFromMegaJSON(megaJSON), [megaJSON]);
}

export function useGetObjectStatuses() {
  return useStore(dbOpsStore, state => state.getObjectStatuses());
}

export function useGetDataObjects() {
  const dataObjects = useStore(dbOpsStore, state => state.getDataObjects());
  return dataObjects;
}

export function useGetDataObject(tag: string | undefined) {
  return useStore(dbOpsStore, state =>
    tag ? state.getDataObject(tag) : undefined
  );
}

const dbOpsGetters = {
  getProcess: dbOps().getProcess,
  getSwimlane: dbOps().getSwimlane,
  getStep: dbOps().getStep,
  getField: dbOps().getField,
  getRule: dbOps().getRule,
  getConditionGroup: dbOps().getConditionGroup,
  getCondition: dbOps().getCondition,
  getBulkAction: dbOps().getBulkAction,
  getEventRule: dbOps().getEventRule,
  getStateRule: dbOps().getStateRule
};

export const dbOpsUtils = {
  useDBProcess: (processId: string) => {
    const process = useInMemoryJSONSelector<ProcessProps | undefined>({
      processId,
      path: "process"
    });
    return process;
  },
  useDBStep: (processId: string, stepId: string) => {
    const step = useInMemoryJSONSelector<Step | undefined>({
      processId,
      path: `steps.${stepId}`
    });
    return step;
  },
  useDBSwimlane: (processId: string, swimlaneId: string) => {
    const swimlane = useInMemoryJSONSelector<Swimlane | undefined>({
      processId,
      path: `swimlanes.${swimlaneId}`
    });
    return swimlane;
  },
  useDBField: (processId: string, fieldId: string) => {
    const field = useInMemoryJSONSelector<Field | undefined>({
      processId,
      path: `fields.${fieldId}`
    });
    return field;
  },
  useDBRule: (processId: string, ruleId: string) => {
    const rule = useInMemoryJSONSelector<Rule | undefined>({
      processId,
      path: `rules.${ruleId}`
    });
    return rule;
  },
  useDBComponent: (
    processId: string,
    componentId?: string, // TODO: Why undefined?
    componentKey?: ComponentKey
  ) => {
    const megaJSON = useStore(dbOpsStore, state => state.megaJSON);
    const process = megaJSON.processes[processId];
    const component =
      componentKey === ComponentKey.PROCESS
        ? process[componentKey]
        : process[componentKey!]?.[componentId!];
    return component;
  },
  useDBConditionGroups: (processId: string) => {
    const conditionGroups = useInMemoryJSONSelector<
      Record<string, ConditionGroupData>
    >({
      processId,
      path: "conditionGroups"
    });
    return useMemo(() => {
      return toArray(conditionGroups);
    }, [conditionGroups]);
  },
  useDBConditions: (processId: string) => {
    const conditions = useInMemoryJSONSelector<Record<string, ConditionData>>({
      processId,
      path: "conditions"
    });

    return useMemo(() => {
      return toArray(conditions);
    }, [conditions]);
  },
  useDBSwimlanes: (processId: string, swimlaneIds: string[]) => {
    const allSwimlanes = useInMemoryJSONSelector<Record<string, Swimlane>>({
      processId,
      path: "swimlanes"
    });
    return useMemo(() => {
      if (!swimlaneIds.length) return [];

      return swimlaneIds
        .map(swimlaneId => allSwimlanes[swimlaneId])
        .filter(swimlane => !!swimlane);
    }, [allSwimlanes, swimlaneIds]);
  },
  useDBSteps: (processId: string, stepIds?: string[]) => {
    const steps = useInMemoryJSONSelector<Record<string, Step>>({
      processId,
      path: "steps"
    });
    return useMemo(() => {
      if (!stepIds?.length) return [];

      return stepIds.map(stepId => steps[stepId]).filter(step => !!step);
    }, [steps, stepIds]);
  },
  useDBFields: (processId: string, fieldIds: string[]) => {
    const allFields = useInMemoryJSONSelector<Record<string, Field>>({
      processId,
      path: "fields"
    });
    return useMemo(() => {
      if (!fieldIds.length) return [];

      return fieldIds
        .map(fieldId => allFields[fieldId])
        .filter(field => !!field);
    }, [allFields, fieldIds]);
  },
  useDBRules: (processId: string, ruleIds?: string[]) => {
    const allRules = useInMemoryJSONSelector<Record<string, Rule>>({
      processId,
      path: "rules"
    });
    return useMemo(() => {
      if (!ruleIds?.length) return [];

      return ruleIds.map(ruleId => allRules[ruleId]).filter(rule => !!rule);
    }, [allRules, ruleIds]);
  },
  useDBEventRules: (processId: string) => {
    const eventRules = useInMemoryJSONSelector<Record<string, Rule>>({
      processId,
      path: "eventRules"
    });
    return eventRules;
  },
  useDBStateRules: (processId: string) => {
    const stateRules = useInMemoryJSONSelector<Record<string, Rule>>({
      processId,
      path: "stateRules"
    });
    return stateRules;
  },

  dbOpsGetters
};
